// clientBuild — A5 (アナリティクス計装修理, analytics-operations-2026-08-13.md
// §2 表 A5) 用の `room.join.clientBuild` 版数文字列.
//
// Purpose:
// - 本番 90 日分の調査で `client_build` が全件 null だったのは、受信側
//   (protocol / server) は完成済みだが「web パッケージ内に `clientBuild` の
//   文字列が 1 件も存在せず、一度も送っていない」ためだった (同メモ §1.1)。
// - 用途はビルド別の不具合切り分けなので、**どのビルドか一意に分かる値**を
//   `room.join` の度に送る (referrer と違い「入口計測」ではないので、内部
//   reconnect / resume-fail fallback join でも毎回送ってよい — SessionProvider
//   参照)。
//
// 値の構成 (`web@<version>+<sha>[-dev]`):
// - `<version>` = package.json version. `vite.config.ts` の `define` で
//   `__APP_VERSION__` として build 時に埋め込む (= 新設。従来 web に版数
//   埋め込みの土台が無かった、同メモ §2 表 A5)。
// - `<sha>` = 既存の `VITE_SENTRY_RELEASE` env を再利用. CI / deploy.sh が
//   `git rev-parse HEAD` で注入し Sentry release tag に使っている値と同じ
//   (infra/nginx/Dockerfile 参照)。新規に git-in-Docker-context 問題を
//   踏まずに「どの commit の build か」を得られる。未設定 (local dev 等)
//   なら省略する。
// - `-dev` suffix は `import.meta.env.DEV` (Vite dev server) のときだけ付く
//   — 「dev ビルドと本番ビルドが区別できると望ましい」(同メモ §2 表 A5) を
//   満たす。`vite build` (production) では付かない。
//
// テスト方針: `formatClientBuild` を pure 関数として切り出し、env 分岐は
// `getClientBuild()` に閉じ込める (initSentry / sentryInit.ts と同じ流儀 —
// 呼び出し側が env 値を明示的に渡す形にして、深い場所で `import.meta.env` を
// 直接読まない).

/**
 * Pure formatter — no `import.meta.env` access, easy to unit test.
 *
 * @param version - `__APP_VERSION__` (package.json version, embedded via
 *   `vite.config.ts`'s `define`).
 * @param sha - `VITE_SENTRY_RELEASE` (full git SHA, or `undefined` /
 *   empty when unset). Truncated to 7 chars to keep the field short (no
 *   server-side length cap on `clientBuild`, but there's no reason to send
 *   a 40-char hash for a human-skimmed field).
 * @param isDev - `import.meta.env.DEV`.
 */
export const formatClientBuild = (
  version: string,
  sha: string | undefined,
  isDev: boolean,
): string => {
  const shaPart = typeof sha === 'string' && sha.length > 0 ? `+${sha.slice(0, 7)}` : '';
  const devSuffix = isDev ? '-dev' : '';
  return `web@${version}${shaPart}${devSuffix}`;
};

/**
 * Resolve the build-time version / SHA / dev-flag and format them. Cheap
 * (string concat, no I/O) — call once per `room.join`, no need to cache.
 */
export const getClientBuild = (): string =>
  formatClientBuild(__APP_VERSION__, import.meta.env.VITE_SENTRY_RELEASE, import.meta.env.DEV);
